import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, resolveLinkProps, isColorDark } from '../utils';
import SectionHeader from './SectionHeader';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

export default function ProductSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'split';
    const currency = content.currency || 'S/';
    const specs = content.specs || [];
    const features = (content.features || '').split('\n').filter(Boolean);

    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const mediaItem = resolveItemMedia(content, mediaMode, mediaFolders);

    const sectionButtons = content.buttons && content.buttons.length > 0
        ? content.buttons
        : content.cta_text ? [{ text: content.cta_text, link: content.cta_link || '#contact', style: 'solid', icon: '', bg_color: '', text_color: '' }] : [];

    const mainImage = mediaItem.resolvedImage;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const hasDiscount = content.original_price && parseFloat(content.original_price) > parseFloat(content.price || 0);
    const discountPercent = hasDiscount ? Math.round((1 - parseFloat(content.price) / parseFloat(content.original_price)) * 100) : 0;

    // ── Gallery-zoom variant ──
    if (variant === 'gallery-zoom') {
        const galleryImages = content.gallery_images || [];
        const allImages = mainImage ? [mainImage, ...galleryImages] : galleryImages;
        const [selectedImg, setSelectedImg] = useState(0);
        const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
        const [zooming, setZooming] = useState(false);
        const currentImg = allImages[selectedImg] || mainImage || '';

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`flex flex-col md:flex-row gap-10 items-start transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                        {/* Gallery */}
                        <div className="md:w-1/2">
                            <div className={`${card} overflow-hidden relative cursor-crosshair`}
                                onMouseEnter={() => setZooming(true)}
                                onMouseLeave={() => setZooming(false)}
                                onMouseMove={e => { const rect = e.currentTarget.getBoundingClientRect(); setZoomPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 }); }}>
                                <div className="aspect-square overflow-hidden">
                                    <img src={currentImg} alt={content.name || ''} className={`w-full h-full ${fitClass} transition-transform duration-300`} loading="lazy" decoding="async" />
                                </div>
                                {zooming && currentImg && (
                                    <div className="absolute inset-0 pointer-events-none z-10 hidden md:block"
                                        style={{ backgroundImage: `url(${currentImg})`, backgroundSize: '200%', backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`, backgroundRepeat: 'no-repeat' }} />
                                )}
                            </div>
                            {allImages.length > 1 && (
                                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                                    {allImages.map((img, i) => (
                                        <button key={i} onClick={() => setSelectedImg(i)}
                                            className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${i === selectedImg ? 'ring-2 ring-offset-1' : 'opacity-60 hover:opacity-100'}`}
                                            style={{ borderColor: i === selectedImg ? s.accent : 'transparent', ringColor: s.accent }}>
                                            <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Info */}
                        <div className="md:w-1/2">
                            {hasDiscount && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: config.badge_color || '#EF4444' }}>-{discountPercent}% OFF</span>}
                            <h3 className="text-2xl md:text-3xl font-bold mt-2" style={{ color: s.text }}>{content.name}</h3>
                            {content.description && <p className="text-base mt-3 leading-relaxed" style={{ color: s.textSub }}>{content.description}</p>}
                            <div className="flex items-baseline gap-3 mt-4">
                                <span className="text-3xl font-bold" style={{ color: s.accent }}>{currency} {content.price}</span>
                                {hasDiscount && <span className="text-lg line-through" style={{ color: s.textSub }}>{currency} {content.original_price}</span>}
                            </div>
                            {specs.length > 0 && (
                                <div className="mt-5 pt-5 space-y-3 border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
                                    {specs.map((sp, i) => (
                                        <div key={i} className="flex justify-between text-sm">
                                            <span style={{ color: s.textSub }}>{sp.label}</span>
                                            <span className="font-medium" style={{ color: s.text }}>{sp.value}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {features.length > 0 && (
                                <div className="mt-5 space-y-2">
                                    {features.map((f, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm">
                                            <svg className="w-4 h-4 shrink-0" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                                            <span style={{ color: s.text }}>{f}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {sectionButtons.length > 0 && (
                                <div className="flex flex-wrap gap-3 mt-6">
                                    {sectionButtons.map((btn, i) => {
                                        const btnStyle = btn.style || 'solid';
                                        const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                        const lp = resolveLinkProps(btn.link || '#contact');
                                        const customBg = btn.bg_color;
                                        const customText = btn.text_color;
                                        let cls = 'px-8 py-3 font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                                        let style = {};
                                        cls += btnStyle === 'pill' ? 'rounded-full ' : 'rounded-lg ';
                                        if (btnStyle === 'solid' || btnStyle === 'pill') {
                                            if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#ffffff' : '#111827'); cls += 'shadow-lg hover:shadow-xl hover:opacity-90'; }
                                            else { style.backgroundColor = s.accent; style.color = '#ffffff'; cls += 'shadow-lg hover:shadow-xl hover:opacity-90'; }
                                        } else if (btnStyle === 'outline') {
                                            if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2 hover:opacity-80'; }
                                            else { style.color = s.accent; style.borderColor = s.accent; cls += 'border-2 hover:opacity-80'; }
                                        } else { style.color = customText || s.text; cls += 'hover:opacity-80'; }
                                        if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                        return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Centered variant ──
    if (variant === 'centered') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`${card} max-w-3xl mx-auto overflow-hidden transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                        <MediaRenderer item={mediaItem} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[4/3]" rounded="" />
                        <div className="p-8 text-center">
                            {hasDiscount && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: config.badge_color || '#EF4444' }}>-{discountPercent}%</span>}
                            <h3 className="text-2xl font-bold mt-3" style={{ color: s.text }}>{content.name}</h3>
                            {content.description && <p className="text-base mt-2 max-w-lg mx-auto" style={{ color: s.textSub }}>{content.description}</p>}
                            <div className="flex items-center justify-center gap-3 mt-4">
                                <span className="text-3xl font-bold" style={{ color: s.accent }}>{currency} {content.price}</span>
                                {hasDiscount && <span className="text-lg line-through" style={{ color: s.textSub }}>{currency} {content.original_price}</span>}
                            </div>
                            {features.length > 0 && (
                                <div className="flex flex-wrap justify-center gap-2 mt-5">
                                    {features.map((f, i) => (
                                        <span key={i} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }}>
                                            <svg className="w-3 h-3" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                                            {f}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {sectionButtons.length > 0 && (
                                <div className="flex flex-wrap justify-center gap-3 mt-6">
                                    {sectionButtons.map((btn, i) => {
                                        const btnStyle = btn.style || 'solid';
                                        const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                        const lp = resolveLinkProps(btn.link || '#contact');
                                        const customBg = btn.bg_color;
                                        const customText = btn.text_color;
                                        let cls = 'px-8 py-3 font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                                        let style = {};
                                        cls += btnStyle === 'pill' ? 'rounded-full ' : 'rounded-lg ';
                                        if (btnStyle === 'solid' || btnStyle === 'pill') {
                                            if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#ffffff' : '#111827'); cls += 'shadow-lg hover:shadow-xl hover:opacity-90'; }
                                            else { style.backgroundColor = s.accent; style.color = '#ffffff'; cls += 'shadow-lg hover:shadow-xl hover:opacity-90'; }
                                        } else if (btnStyle === 'outline') {
                                            if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2 hover:opacity-80'; }
                                            else { style.color = s.accent; style.borderColor = s.accent; cls += 'border-2 hover:opacity-80'; }
                                        } else { style.color = customText || s.text; cls += 'hover:opacity-80'; }
                                        if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                        return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Split (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`flex flex-col md:flex-row gap-10 items-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    {/* Media */}
                    <div className="md:w-1/2">
                        <div className={`${card} overflow-hidden`}>
                            <MediaRenderer item={mediaItem} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[4/3]" rounded="" />
                        </div>
                    </div>

                    {/* Info */}
                    <div className="md:w-1/2">
                        {hasDiscount && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: config.badge_color || '#EF4444' }}>-{discountPercent}% OFF</span>}
                        <h3 className="text-2xl md:text-3xl font-bold mt-2" style={{ color: s.text }}>{content.name}</h3>
                        {content.description && <p className="text-base mt-3 leading-relaxed" style={{ color: s.textSub }}>{content.description}</p>}

                        <div className="flex items-baseline gap-3 mt-4">
                            <span className="text-3xl font-bold" style={{ color: s.accent }}>{currency} {content.price}</span>
                            {hasDiscount && <span className="text-lg line-through" style={{ color: s.textSub }}>{currency} {content.original_price}</span>}
                        </div>

                        {specs.length > 0 && (
                            <div className="mt-5 pt-5 space-y-3 border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
                                {specs.map((sp, i) => (
                                    <div key={i} className="flex justify-between text-sm">
                                        <span style={{ color: s.textSub }}>{sp.label}</span>
                                        <span className="font-medium" style={{ color: s.text }}>{sp.value}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {features.length > 0 && (
                            <div className="mt-5 space-y-2">
                                {features.map((f, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                        <svg className="w-4 h-4 shrink-0" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                                        <span style={{ color: s.text }}>{f}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {sectionButtons.length > 0 && (
                            <div className="flex flex-wrap gap-3 mt-6">
                                {sectionButtons.map((btn, i) => {
                                    const btnStyle = btn.style || 'solid';
                                    const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                    const lp = resolveLinkProps(btn.link || '#contact');
                                    const customBg = btn.bg_color;
                                    const customText = btn.text_color;
                                    let cls = 'px-8 py-3 font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                                    let style = {};
                                    cls += btnStyle === 'pill' ? 'rounded-full ' : 'rounded-lg ';
                                    if (btnStyle === 'solid' || btnStyle === 'pill') {
                                        if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#ffffff' : '#111827'); cls += 'shadow-lg hover:shadow-xl hover:opacity-90'; }
                                        else { style.backgroundColor = s.accent; style.color = '#ffffff'; cls += 'shadow-lg hover:shadow-xl hover:opacity-90'; }
                                    } else if (btnStyle === 'outline') {
                                        if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2 hover:opacity-80'; }
                                        else { style.color = s.accent; style.borderColor = s.accent; cls += 'border-2 hover:opacity-80'; }
                                    } else { style.color = customText || s.text; cls += 'hover:opacity-80'; }
                                    if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                    return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
